import {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {useNotifications} from '../../../shared';
import {fundraiserImageApi} from '../../fundraiserImages/api';

interface UseDocumentUploadResult {
  uploadId: string | null;
  fileName: string | null;
  fileSize: number | null;
  isUploading: boolean;
  uploadDocument: (file: File) => Promise<void>;
  resetUpload: () => void;
}

/**
 * Утилита для прямой загрузки файла
 */
const uploadFileDirectly = async (file: File, uploadUrl: string): Promise<void> => {
  const buffer = await file.arrayBuffer();
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: buffer,
  });

  if (!response.ok) {
    let errorBody = 'Unknown error during direct upload.';
    try {
      errorBody = await response.text();
    } catch (e) {
      // Ignore error reading body
    }
    throw new Error(`Direct upload failed with status ${response.status}: ${errorBody}`);
  }
};

export const useDocumentUpload = (): UseDocumentUploadResult => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const {createNotification} = useNotifications();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadId(null);
      setFileName(null);
      setFileSize(null);

      // Подготавливаем загрузку
      const response = await fundraiserImageApi.prepareUpload([file.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Prepare upload response is invalid or missing uploadUrl');
      }

      const uploadItem = response.items[0];
      setUploadId(uploadItem.id);
      setFileName(file.name);
      setFileSize(file.size);

      // Загружаем файл
      await uploadFileDirectly(file, uploadItem.url);
    },
    onSuccess: () => {
      createNotification({
        name: 'document-upload-success',
        title: 'Документ загружен',
        theme: 'success',
      });
    },
    onError: (error: Error) => {
      createNotification({
        name: 'document-upload-error',
        title: 'Ошибка загрузки документа',
        content: error.message,
        theme: 'danger',
      });
      setUploadId(null);
      setFileName(null);
      setFileSize(null);
    },
  });

  const resetUpload = () => {
    setUploadId(null);
    setFileName(null);
    setFileSize(null);
    uploadMutation.reset();
  };

  return {
    uploadId,
    fileName,
    fileSize,
    isUploading: uploadMutation.isPending,
    uploadDocument: uploadMutation.mutateAsync,
    resetUpload,
  };
};
